import { formatEther } from 'viem'

import { MAIN_ACTION } from '../../constants/actions'
import {
  ENABLE_SALE_RESULTS,
  SALE_PRICING,
  TIME_BASED_BONUS,
  TOTAL_AMOUNT_TO_RAISE
} from '../../constants/app'
import { mergeToNewObject } from '../../util/object.helpers'
import { floorNumber } from '../../util/numbers.helpers'
import {
  getAmountBasedLabelSum,
  getReferralBasedLabelSum,
  getTimeBasedKey,
  isSaleEnded
} from '../../util/app.helpers'

const mainReducer = (state, { type, payload }) => {
  switch (type) {
    case MAIN_ACTION.GET_WALLET:
      return mergeToNewObject(state, { loading: true })

    case MAIN_ACTION.GET_WALLET_SUCCESS:
      const {
        nft_count,
        raffle_pre_won,
        raffle_pub_won,
        eth_price
        // pub_whitelist_count
      } = payload
      const ethPrice = eth_price || SALE_PRICING.DEFAULT_ETH_PRICE
      return mergeToNewObject(state, {
        nftCount: nft_count || 0,
        preSaleTicketsWon: raffle_pre_won || 0,
        publicSaleTicketsWon: raffle_pub_won || 0,
        ethPrice,
        preSaleDepositPrice: SALE_PRICING.PRE_SALE_DEPOSIT / ethPrice,
        publicSaleDepositPrice: SALE_PRICING.PUBLIC_SALE_DEPOSIT / ethPrice,
        // publicRaffleGuaranteed: pub_whitelist_count || 0,
        loading: false
      })

    case MAIN_ACTION.GET_WALLET_FAILED:
      return mergeToNewObject(state, { loading: false, error: payload })

    case MAIN_ACTION.CHANGE_STATE:
      return mergeToNewObject(
        state,
        { [payload.property]: payload.value },
        handleChangeState(payload)
      )

    case MAIN_ACTION.UPDATE_DATA:
      const [userDepositData] = payload
      let userDeposit = userDepositData?.result
        ? parseFloat(formatEther(userDepositData?.result))
        : 0
      const userDepositValue =
        isSaleEnded() && ENABLE_SALE_RESULTS
          ? state.resultUserDeposit
          : userDeposit
      const preRaffleDeposited = floorNumber(
        userDepositValue / state.preSaleDepositPrice
      )
      const publicRaffleDeposited = floorNumber(
        userDepositValue / state.publicSaleDepositPrice
      )
      const ticketsWonETH =
        state.preSaleDepositPrice * state.preSaleTicketsWon +
        state.publicSaleDepositPrice * state.publicSaleTicketsWon
      return mergeToNewObject(state, {
        userDeposit,
        preRaffleDeposited,
        publicRaffleDeposited,
        preSaleQBX: SALE_PRICING.PRE_SALE_QBX_VALUE * state.preSaleTicketsWon,
        publicSaleQBX:
          SALE_PRICING.PUBLIC_SALE_QBX_VALUE * state.publicSaleTicketsWon,
        ticketsWonETH
      })

    case MAIN_ACTION.RESET_STATE:
      return {
        ...mainInitialState,
        timeline: state.timeline,
        totalRaised: state.totalRaised
      }

    case MAIN_ACTION.GET_ACCOUNT_WALLET:
      return mergeToNewObject(state, {
        loading: true,
        hardRefresh: payload.hardRefresh
      })

    case MAIN_ACTION.GET_ACCOUNT_WALLET_SUCCESS:
      return mergeToNewObject(
        state,
        handleAccountWalletInfoResponse(payload, state)
      )

    case MAIN_ACTION.GET_ACCOUNT_WALLET_FAILED:
      return mergeToNewObject(state, { loading: false, error: payload })

    case MAIN_ACTION.POST_REFERRAL_CODE_SUCCESS:
      return mergeToNewObject(state, {
        referred: payload?.referred || true
      })

    case MAIN_ACTION.GET_RECENT_DEPOSITS_SUCCESS:
      return mergeToNewObject(state, {
        timeline: payload
      })

    default:
      return state
  }
}

const mainInitialState = {
  timeline: [],
  preSaleDepositPrice:
    SALE_PRICING.PRE_SALE_DEPOSIT / SALE_PRICING.DEFAULT_ETH_PRICE,
  publicSaleDepositPrice:
    SALE_PRICING.PUBLIC_SALE_DEPOSIT / SALE_PRICING.DEFAULT_ETH_PRICE,
  ethPrice: SALE_PRICING.DEFAULT_ETH_PRICE,
  nftCount: 0,
  hardRefresh: false,
  targetAmount: TOTAL_AMOUNT_TO_RAISE,
  userDeposit: 0,
  totalRaised: 0,
  preRaffleGuaranteed: 0,
  preRaffleDeposited: 0,
  publicRaffleDeposited: 0,
  preSaleTicketsWon: 0,
  publicSaleTicketsWon: 0,
  resultUserDeposit: 0,
  ticketsWonETH: 0,
  preSaleQBX: 0,
  publicSaleQBX: 0,
  amount: '',
  loading: false,
  depositProcessing: false,
  refundProcessing: false,
  // account wallet info
  rank: '',
  points: 0,
  finalPoints: 0,
  referred: null,
  communityName: '',
  communityPoints: 0,
  communityRank: '',
  individualPoints: 0,
  timeBasedPoints: 0,
  timeBasedPosition: '',
  amountBasedPoints: 0,
  amountBasedPosition: '',
  referralBasedPoints: 0,
  referralBasedPosition: '',
  friendsReferred: 0,
  referPointsEarned: 0,
  timeBasedPointsFinal: 0,
  timeBasedPositionFinal: '',
  amountBasedPointsFinal: 0,
  amountBasedPositionFinal: '',
  referralBasedPointsFinal: 0,
  referralBasedPositionFinal: '',
  saleClaimed: false,
  finalMs: {},
  error: {}
}

const MAIN_PROFILE_DISPATCH = {
  AMOUNT: 'amount',
  TOTAL_RAISED: 'totalRaised',
  DEPOSIT_PROCESSING: 'depositProcessing',
  REFUND_PROCESSING: 'refundProcessing',
  REFUND: 'refund',
  ERROR: 'error'
}

export { mainReducer, mainInitialState, MAIN_PROFILE_DISPATCH }

const handleChangeState = ({ property }) => {
  if (property === MAIN_PROFILE_DISPATCH.ERROR) {
    return {
      depositProcessing: false,
      refundProcessing: false
    }
  } else if (
    [
      MAIN_PROFILE_DISPATCH.DEPOSIT_PROCESSING,
      MAIN_PROFILE_DISPATCH.REFUND_PROCESSING
    ].includes(property)
  ) {
    return {
      error: {}
    }
  }
  return {}
}

const handleAccountWalletInfoResponse = (payload, state) => {
  if (!payload?.data) {
    return {}
  }
  const { e, r, p, ref, c, cr, final_p, ms, final_ms, sc } = payload.data
  const timeBasedPosition = getTimeBasedKey(ms?.time)
  const amountBased = getAmountBasedLabelSum(ms?.amount)
  const referralBased = getReferralBasedLabelSum(ms?.ref)
  const timeBasedPositionFinal = getTimeBasedKey(final_ms?.time)
  const amountBasedFinal = getAmountBasedLabelSum(final_ms?.amount)
  const referralBasedFinal = getReferralBasedLabelSum(final_ms?.ref)
  return {
    hardRefresh: state.hardRefresh || (state.points === 0 && p > 0),
    rank: r,
    resultUserDeposit: e || 0,
    points: p || 0,
    finalPoints: final_p || 0,
    referred: Boolean(ref),
    communityName: c,
    communityPoints: final_ms?.community || 0,
    communityRank: cr,
    individualPoints: final_ms?.individual || 0,
    timeBasedPoints: TIME_BASED_BONUS[timeBasedPosition] || 0,
    timeBasedPosition,
    amountBasedPoints: amountBased.value,
    amountBasedPosition: amountBased.label.join(' | '),
    referralBasedPoints: referralBased.value,
    referralBasedPosition: referralBased.label.join(' | '),
    timeBasedPointsFinal: TIME_BASED_BONUS[timeBasedPositionFinal] || 0,
    timeBasedPositionFinal,
    finalMs: final_ms,
    amountBasedPointsFinal: amountBasedFinal.value,
    amountBasedPositionFinal: amountBasedFinal.label.join(' | '),
    referralBasedPointsFinal: referralBasedFinal.value,
    referralBasedPositionFinal: referralBasedFinal.label.join(' | '),
    friendsReferred: ms?.ref_c || 0,
    referPointsEarned: ms?.ref_s || 0,
    saleClaimed: sc,
    loading: false
  }
}
